import { match } from 'ts-pattern';
import { TixCurrencyTypes } from '../types/TixTypes';

export async function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(() => resolve(undefined), ms));
}

function getFormatter(currency: string, digits?: number) {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency,
		maximumFractionDigits: digits,
		minimumFractionDigits: digits,
	});
}

export function formatCurrency(amount: number, currency: TixCurrencyTypes | 'EUR', digits?: number) {
	return match(currency)
		.otherwise(() => getFormatter(currency, digits))
		.format(amount);
}

export function normalizeCurrency(value: string): string {
	value = value.trim();

	// NOTE Both present so decide by last separator
	if (value.includes(',') && value.includes('.')) {
		// NOTE: EU Format so decimal comma and thousand dot
		if (value.lastIndexOf(',') > value.lastIndexOf('.')) {
			value = value.replace(/\./g, '').replace(',', '.');
		}
		// NOTE: US format
		else {
			value = value.replace(/,/g, '');
		}
	}
	// NOTE: Only comma so decimal separator
	else if (value.includes(',')) {
		value = value.replace(',', '.');
	}

	return value;
}

export function checkCurrencyFormatPartly(value: string): string | undefined {
	if (value === '') return value;

	const cleanedValue = value.replaceAll(' ', '');
	const regex = /^-?((\d{1,3}(,\d{3})*|\d+)(\.\d{0,2})?)?$/;
	if (!regex.test(cleanedValue)) return;
	return cleanedValue;
}

export function checkCurrencyFormatFull(value: string): string | undefined {
	if (value === '') return value;

	const regex = /^-?(\d{1,3}(,\d{3})*|\d+)(\.\d{1,2})?$/;
	if (!regex.test(value)) return;
	return normalizeCurrency(value);
}

export function checkIntFormatFull(value: string): string | undefined {
	if (value === '') return value;

	const regex = /^\d+$/;
	if (!regex.test(value)) return;
	return value;
}

export function checkDateSchemaFormatPartly(value: string): string | undefined {
	const regex = /^\d{0,2}(-\d{0,2})?(-\d{0,4})?$/;

	const cleanedValue = value.replaceAll(' ', '');
	if (!regex.test(cleanedValue)) return;
	return cleanedValue;
}

export function checkDateSchemaFormatFull(value: string): string | undefined {
	if (value === '') return value;

	const regex = /^\d{2}-\d{2}-\d{4}$/;
	if (!regex.test(value)) return;
	return value;
}
type KeysOfUnion<T> = T extends T ? keyof T : never;

export function objectWithoutProperties<T, R>(obj: T, keys: KeysOfUnion<T>[]): R {
	const target: Partial<T> = {};
	for (const i in obj) {
		if (keys.includes(i as unknown as KeysOfUnion<T>)) continue;
		target[i] = obj[i];
	}
	return target as R;
}

export function convertEnumsToOptions<T extends Record<string, string | number>, U extends Record<keyof T, string>>(
	valueEnums: T,
	textEnums: U,
): { value: T[keyof T]; text: string }[] {
	return Object.keys(valueEnums).map((mode) => {
		const key = mode as keyof T;
		return {
			value: valueEnums[key],
			text: textEnums[key],
		};
	});
}

export function convertObjectToOptions<T extends Record<string, string>>(
	valueEnums: T,
): { value: keyof T; text: T[keyof T] }[] {
	return Object.entries(valueEnums).map(([key, value]) => {
		return {
			value: key as keyof T,
			text: value as T[keyof T],
		};
	});
}

export function getOptionList(defaultOption: string, options: { value: string; text: string }[]) {
	return [{ value: '', text: `Select ${defaultOption}` }, ...options];
}

export function getQuarterObj(dateStr: string | null): {
	name: string;
	id: string;
} {
	if (!dateStr) return { name: `Uncategorized Sales`, id: `uncategorized` };

	const [, monthStr, yearStr] = dateStr.split('-');

	const month = Number(monthStr);
	const year = Number(yearStr);

	let quarter: 'q1' | 'q2' | 'q3' | 'q4';

	if (month <= 3) quarter = 'q1';
	else if (month <= 6) quarter = 'q2';
	else if (month <= 9) quarter = 'q3';
	else quarter = 'q4';

	return { name: `${quarter.toUpperCase()} - ${year}`, id: `${year}-${quarter}` };
}

export function getOffset(element: HTMLDivElement) {
	if (!element.getClientRects().length) {
		return { top: 0, left: 0, height: 0 };
	}

	const rect = element.getBoundingClientRect();
	const win = element.ownerDocument.defaultView;
	return {
		top: rect.top + win!.pageYOffset,
		left: rect.left + win!.pageXOffset,
		height: rect.height,
	};
}

export function downloadCSVFile(fileData: string, fileName: string) {
	const blob = new Blob([fileData], { type: 'text/csv;charset=utf-8;' });

	const url = URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', fileName);

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}
